/**
 * ClockOutFlow — Driver Portal clock-out component
 * Extracted from DriverApp.jsx (V2-03.12h)
 *
 * Three states: INPUT → CONFIRM → RECEIPT
 * Captures stops, ending mileage, truck issues.
 * All request payloads and bonus display logic preserved exactly.
 */

import React, { useState } from 'react';
import { C, FONT, GLASS, GLASS_STRONG, INPUT, BTN, BTN_DISABLED } from '../driver-tokens.js';
import { apiPost } from '../auth/driver-session.js';

// Clock-out flow — 3 steps: enter stops/ending mileage/truck issues → confirm → receipt
interface DailyLog {
  stops?: number;
  miles?: number | string;
  threshold_used?: string | number;
  stop_bonus?: string | number;
}

function ClockOutFlow({ onDone }: { onDone: () => void }) {
  var [step, setStep] = useState<'INPUT' | 'CONFIRM' | 'RECEIPT'>('INPUT');
  var [stops, setStops] = useState("");
  var [endingMileage, setEndingMileage] = useState("");
  var [truckIssues, setTruckIssues] = useState("");
  var [saving, setSaving] = useState(false);
  var [result, setResult] = useState<DailyLog | null>(null);
  var [error, setError] = useState("");

  var stopsNum = parseInt(stops) || 0;
  var endMiles = parseInt(endingMileage) || 0;

  function handleConfirm() {
    setSaving(true); setError("");
    apiPost<{ success?: boolean; daily_log?: DailyLog; message?: string; error?: string }>("/api/clock/out", {
      stops: stopsNum,
      ending_mileage: endMiles,
      truck_issues: truckIssues.trim() || null,
      geo: null
    }).then(function(res) {
      setSaving(false);
      if (res.success) {
        setResult(res.daily_log || { stops: stopsNum, miles: 0, stop_bonus: 0, threshold_used: 100 });
        setStep("RECEIPT");
      } else {
        setError(res.message || res.error || "Clock-out failed");
      }
    }).catch(function() { setSaving(false); setError("Connection error"); });
  }

  if (step === "RECEIPT" && result) {
    var bonus = parseFloat(String(result.stop_bonus)) || 0;
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20, textAlign: "center" } },
        React.createElement("div", { style: { fontSize: 48, marginBottom: 16 } }, "\u2713"),
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 22, color: C.text, marginBottom: 8 } }, "Clocked Out"),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.silver, marginBottom: 20 } }, new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })),
        React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8, marginBottom: 24, textAlign: "center" } },
          React.createElement("div", null,
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 24, color: C.text } }, result.stops),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Stops")
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 24, color: C.text } }, Math.round(parseFloat(String(result.miles)) || 0)),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Miles")
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 24, color: C.text } }, result.threshold_used || "\u2014"),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Threshold")
          ),
          React.createElement("div", null,
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 24, color: bonus > 0 ? C.gold : C.text } }, "$" + bonus.toFixed(2)),
            React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 10, color: C.textMuted } }, "Bonus")
          )
        ),
        bonus > 0 && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 14, color: C.gold, background: "rgba(201,162,39,0.15)", padding: "10px 16px", borderRadius: 10, marginBottom: 16, fontWeight: 700 } }, "Stop bonus: $" + bonus.toFixed(2)),
        React.createElement("button", { style: BTN, onClick: onDone }, "Done")
      )
    );
  }

  if (step === "CONFIRM") {
    return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
      React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20, textAlign: "center" } },
        React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, marginBottom: 16 } }, "Confirm Clock Out"),
        React.createElement("div", { style: { ...GLASS, padding: 16, marginBottom: 16, textAlign: "left" } },
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: 8 } },
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.textMuted } }, "Stops"),
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 16, color: C.text } }, stopsNum)
          ),
          React.createElement("div", { style: { display: "flex", justifyContent: "space-between", marginBottom: 8 } },
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.textMuted } }, "Ending Mileage"),
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 16, color: C.text } }, endMiles)
          ),
          truckIssues.trim() && React.createElement("div", { style: { display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)" } },
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.textMuted } }, "Truck Issues"),
            React.createElement("span", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.text, textAlign: "right", maxWidth: 180, wordBreak: "break-word" } }, truckIssues.trim())
          )
        ),
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.silver, marginBottom: 16 } }, "Miles and bonus will be calculated automatically."),
        error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, marginBottom: 12 } }, error),
        React.createElement("button", { style: saving ? BTN_DISABLED : { ...BTN, background: C.red, color: "#fff" }, disabled: saving, onClick: handleConfirm }, saving ? "Clocking out..." : "Confirm Clock Out"),
        React.createElement("button", { style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: function() { setStep("INPUT"); } }, "Go Back")
      )
    );
  }

  // INPUT step
  return React.createElement("div", { style: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.dark } },
    React.createElement("div", { style: { ...GLASS_STRONG, padding: 32, maxWidth: 380, width: "100%", borderRadius: 20 } },
      React.createElement("div", { style: { fontFamily: FONT.headline, fontSize: 20, color: C.text, textAlign: "center", marginBottom: 20 } }, "End of Day"),
      React.createElement("div", { style: { marginBottom: 16 } },
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Total Stops *"),
        React.createElement("input", {
          type: "number", inputMode: "numeric", style: { ...INPUT, fontSize: 24, textAlign: "center" },
          value: stops, onChange: function(e) { setStops(e.target.value); }, placeholder: "0", autoFocus: true
        })
      ),
      React.createElement("div", { style: { marginBottom: 16 } },
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Ending Mileage *"),
        React.createElement("input", {
          type: "number", inputMode: "numeric", style: { ...INPUT, fontSize: 24, textAlign: "center" },
          value: endingMileage, onChange: function(e) { setEndingMileage(e.target.value); }, placeholder: "0"
        })
      ),
      React.createElement("div", { style: { marginBottom: 20 } },
        React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 } }, "Truck Issues (optional)"),
        React.createElement("input", {
          style: INPUT,
          value: truckIssues, onChange: function(e) { setTruckIssues(e.target.value); }, placeholder: "Any issues to report..."
        })
      ),
      error && React.createElement("div", { style: { fontFamily: FONT.mono, fontSize: 13, color: C.red, textAlign: "center", marginBottom: 12 } }, error),
      React.createElement("button", { style: (stops && endingMileage) ? BTN : BTN_DISABLED, disabled: !stops || !endingMileage, onClick: function() {
        if (!stops || parseInt(stops) < 0) { setError("Stops is required"); return; }
        if (!endingMileage || parseInt(endingMileage) <= 0) { setError("Ending mileage is required"); return; }
        setError(""); setStep("CONFIRM");
      } }, "Continue"),
      React.createElement("button", { style: { ...GLASS, width: "100%", padding: 14, color: C.silver, fontFamily: FONT.mono, fontSize: 14, cursor: "pointer", marginTop: 8, minHeight: 48, textAlign: "center" }, onClick: onDone }, "Cancel")
    )
  );
}

export default ClockOutFlow;
